import type { CreatePicklistBinRequest, SelectedItemDisplay } from '../types';

/**
 * The picklist cart's allocation plan, computed per ITEM rather than per MRS line (14ykpqtnxbv).
 *
 * The cart used to group by MRS, so one item raised on two MRS lines showed as two independent cards
 * with two independent bin dialogs — while the server allocated the item from a single pool. Nothing on
 * either card said the other had already spoken for the units, which is how `PL-202609-00221` was given
 * `ELT-BSB-6..9` twice and became unpostable.
 *
 * This module is the single place that answers "what does this picklist plan to take for this item, and
 * how much for each MRS line". The cart card, the Allocate Bins dialog and the creation review all read it —
 * duplicating the walk is how the original bug comes back in a different shape. Which MRS line each unit
 * serves is decided at pick time (PT-7, derived model), so nothing here pairs a bin with a line on screen.
 */

/** Quantity overrides keyed by `${mrsId}-${itemId}` — the cart's existing key shape. */
export type QuantityOverrides = Record<string, number>;

/** What the stock read said about an item. `null` on-hand means the read has not landed or failed. */
export interface ItemStockFacts {
  /** Raw NetSuite on-hand. */
  onHand: number | null;
  /**
   * Units already allocated to other picklists that have not posted yet. NetSuite still counts these as
   * on hand — a DOP allocation does not reach NetSuite until the MIS posts — so on-hand alone overstates
   * what this picklist can actually take.
   */
  committed: number;
}

export interface PlannedLine {
  item: SelectedItemDisplay;
  /** `${mrsId}-${itemId}` — the cart's key for overrides and removal. */
  key: string;
  /**
   * What this MRS line may still take: `availableQuantity` = Requested − Fulfilled − Pending in
   * Picklists. NOT `requestedQuantity` — on a partly-released line that is larger than the line's real
   * ceiling, and planning against it makes the server refuse at `PicklistService.cs:426` (owner ruling
   * 2026-09-18; the design prototype cannot show this because its data has no released quantities).
   */
  wanted: number;
  /** `wanted` clamped to what is left for the item after the lines above it. */
  cap: number;
  /** What will actually be sent: the user's override clamped to `cap`, or `cap`. */
  pick: number;
  /** True when the user typed a quantity for this line. */
  overridden: boolean;
  /**
   * True when STOCK cannot cover this line — computed from the stock-only walk, never from `pick`.
   * Lowering a quantity by hand is a decision, not a problem: it must never light the badge, tint the
   * row, or count in `Short (n)`.
   */
  short: boolean;
  /** How many units stock cannot cover for this line. 0 unless `short`. */
  shortBy: number;
}

export interface ItemPlan {
  netsuiteItemId: number;
  itemCode: string;
  itemName: string;
  unitOfMeasure: string;
  /** Lines in MRS-line order — the order units are handed out in. */
  lines: PlannedLine[];
  /** Sum of every line's `wanted`. */
  wanted: number;
  /** Sum of every line's `pick` — what the picklist will take for this item. */
  total: number;
  /** Raw NetSuite on-hand, or `null` when the stock read has not landed or failed. */
  onHand: number | null;
  /** Units held by other unposted picklists. */
  committed: number;
  /**
   * `onHand − committed`, floored at 0. This is what the plan is actually built from — the figure the
   * shipped cart already deducted before this redesign, and dropping it would be a regression.
   */
  effectiveAvailable: number | null;
  /** False when the stock read has not landed or failed; the plan then falls back to what was asked. */
  known: boolean;
  /** Units stock cannot cover across the whole item. 0 when it can. */
  shortfall: number;
  /** Distinct MRS numbers composing this item, in first-seen order. */
  mrsNumbers: string[];
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

export function lineKey(item: SelectedItemDisplay): string {
  return `${item.mrsId}-${item.itemId}`;
}

/**
 * Build one item's plan from the MRS lines that selected it.
 *
 * Two walks, deliberately:
 *
 * 1. **Stock walk** — hands out `effectiveAvailable` in line order ignoring every override, and is the
 *    only source of `short` / `shortBy` / `shortfall`. This is what keeps the shortfall naming the same
 *    lines whatever anyone types into the quantity fields.
 * 2. **Pick walk** — hands out the same pool but spends each line's override where one exists, so
 *    lowering an earlier line genuinely frees units for a later one.
 *
 * `lines` must already be in MRS-line order; the caller owns that order because it is also the order the
 * server allocates in.
 */
export function buildItemPlan(
  lines: SelectedItemDisplay[],
  stock: ItemStockFacts,
  overrides: QuantityOverrides,
): ItemPlan {
  const first = lines[0];
  const known = stock.onHand !== null;
  const effectiveAvailable = known ? Math.max(0, stock.onHand! - stock.committed) : null;

  // Walk 1 — stock only. Establishes each line's ceiling and the short flags.
  let remStock = known ? effectiveAvailable! : Infinity;
  const caps = lines.map((item) => {
    const wanted = item.availableQuantity;
    const cap = Math.max(0, Math.min(wanted, remStock));
    if (remStock !== Infinity) remStock -= cap;
    return { wanted, cap };
  });

  // Walk 2 — the real plan. Spends overrides, so a line lowered by hand releases units downstream.
  let remPick = known ? effectiveAvailable! : Infinity;
  const planned: PlannedLine[] = lines.map((item, i) => {
    const key = lineKey(item);
    const { wanted } = caps[i];
    const cap = Math.max(0, Math.min(wanted, remPick));
    const override = overrides[key];
    const overridden = override !== undefined;
    const pick = overridden ? clamp(override, 0, cap) : cap;
    if (remPick !== Infinity) remPick -= pick;

    return {
      item,
      key,
      wanted,
      cap,
      pick,
      overridden,
      // From walk 1, never from `pick` — see PlannedLine.short.
      short: caps[i].cap < wanted,
      shortBy: Math.max(0, wanted - caps[i].cap),
    };
  });

  const mrsNumbers: string[] = [];
  for (const line of lines) {
    if (!mrsNumbers.includes(line.mrsNumber)) mrsNumbers.push(line.mrsNumber);
  }

  return {
    netsuiteItemId: first.netsuiteItemId,
    itemCode: first.itemCode,
    itemName: first.itemName,
    unitOfMeasure: first.unitOfMeasure,
    lines: planned,
    wanted: caps.reduce((sum, c) => sum + c.wanted, 0),
    total: planned.reduce((sum, l) => sum + l.pick, 0),
    onHand: stock.onHand,
    committed: stock.committed,
    effectiveAvailable,
    known,
    shortfall: planned.reduce((sum, l) => sum + l.shortBy, 0),
    mrsNumbers,
  };
}

/**
 * PT-7 F7 (O-22, seam S5) — the ONE key for a plan: the server's item-group key, `(NetsuiteItemId,
 * UnitOfMeasure)` of each line's MRS line, compared EXACTLY — no case folding, no trimming. So one cart card
 * becomes one Items-table row after creation, and an item raised in KG and in PCS is two cards and two rows.
 *
 * Takes a plan or a cart line, so a line can find its plan's key. Every per-plan map in the cart (bin
 * overrides, plan statuses, chosen-bin counts, React keys, the Allocate Bins dialog) keys on this. Stock stays
 * per NetSuite item — `getStock`, the stock-detail reads — because NetSuite holds stock per item, not per unit.
 */
export function planKey(x: { netsuiteItemId: number; unitOfMeasure: string | null }): string {
  return `${x.netsuiteItemId}|${x.unitOfMeasure ?? ''}`;
}

/**
 * Group the cart's selected lines by item AND unit (`planKey`) and plan each one.
 *
 * Keyed by `netsuiteItemId`, not by item code: it is what the stock store, the creation payload and the
 * server's own `PicklistBinPool` all key on. Item code is for display only. The unit joins the key because a
 * group's figures are summed, and 50 KG plus 3 PCS is not a quantity (O-22). Two plans of one item read the
 * same stock facts; the server's pool keeps their serials disjoint (one stock copy per NetSuite item).
 *
 * Insertion order is preserved so the cart does not reshuffle as items are ticked.
 */
export function buildItemPlans(
  selectedItems: SelectedItemDisplay[],
  getStockFacts: (netsuiteItemId: number) => ItemStockFacts,
  overrides: QuantityOverrides,
): ItemPlan[] {
  const byPlan = new Map<string, SelectedItemDisplay[]>();
  for (const item of selectedItems) {
    const key = planKey(item);
    const existing = byPlan.get(key);
    if (existing) existing.push(item);
    else byPlan.set(key, [item]);
  }

  const plans: ItemPlan[] = [];
  byPlan.forEach((lines) => {
    plans.push(buildItemPlan(lines, getStockFacts(lines[0].netsuiteItemId), overrides));
  });
  return plans;
}

/**
 * Identity of one bin row: bin + inventory number. Serialized items return one row per serial from the
 * same bin, so `binId` alone is not a row.
 *
 * Exported because the Allocate Bins dialog keys its rows by exactly this composite (`data-row-key`).
 */
export function binIdentity(bin: Pick<CreatePicklistBinRequest, 'binId' | 'inventoryNumberId'>): string {
  return `${bin.binId}:${bin.inventoryNumberId ?? ''}`;
}

/**
 * A saved allocation for one plan: the quantities the user chose in Allocate Bins, one row per (bin, lot or
 * serial). Just the quantities — which MRS line each unit ends up on is decided at pick time, not here.
 */
export interface SavedAllocation {
  bins: CreatePicklistBinRequest[];
}

/**
 * PT-7 F5 — the PROVISIONAL split of a plan's chosen bins across its MRS lines, for the creation payload.
 *
 * The record is still one row per MRS line (DERIVED_MODEL §2), so each line needs its own `bins` array, and
 * the server stores each line's share as that line's slots. The split is provisional: the picker chooses the
 * MRS line at pick time, and the server may move unpicked room between the lines then. No screen shows it.
 *
 * A plain greedy walk — lines in order, rows in order, each line filled to its `pick` before the next gets a
 * unit; a row that straddles a line boundary is split. One entry per (row, line) pairing. At a valid total
 * (the rows sum to the plan) the walk gives every line exactly its `pick`, in positive parts, and a row
 * reaches a line at most once, so the server's per-line validator passes: every entry positive, its bins
 * total its `quantityToPick`, no `(binId, inventoryNumberId)` twice in one line. Across the lines a row's
 * parts sum to exactly its saved quantity, so the split claims no unit the saved allocation did not; a serial
 * row holds 1 and goes whole to one line.
 */
export function splitBinsForPayload(
  plan: ItemPlan,
  bins: CreatePicklistBinRequest[],
): Record<string, CreatePicklistBinRequest[]> {
  const out: Record<string, CreatePicklistBinRequest[]> = {};
  const left = bins.map((bin) => Math.max(0, bin.allocatedQuantity));
  for (const line of plan.lines) {
    let outstanding = Math.max(0, line.pick);
    for (let row = 0; row < bins.length && outstanding > 0; row += 1) {
      const take = Math.min(outstanding, left[row]);
      if (take <= 0) continue;
      (out[line.key] ??= []).push({ ...bins[row], allocatedQuantity: take });
      left[row] -= take;
      outstanding -= take;
    }
  }
  return out;
}

/**
 * The card's composition line. One item raised twice by one MRS reads differently from one item raised
 * by two MRS, and the difference is the whole point of grouping by item.
 */
export function compositionLabel(plan: ItemPlan): string | null {
  const lineCount = plan.lines.length;
  if (lineCount <= 1) return null;
  const mrsCount = plan.mrsNumbers.length;
  return mrsCount > 1
    ? `${lineCount} lines · ${mrsCount} MRS`
    : `${lineCount} MRS lines`;
}

/**
 * The legend pill's short name for an MRS line — what tells two lines of one item apart. The prototype's
 * `crLineTag`: remarks, else end use, else department, else the MRS number; a leading "FOR " dropped; whole
 * words up to 26 characters, then "…". Cutting mid-word would read as a typo rather than a truncation.
 * `full` is the uncut source, for the pill's native title (UI-GUIDELINES §8).
 */
export function lineTag(item: SelectedItemDisplay, max = 26): { tag: string; full: string } {
  const full = String(
    item.remarks || item.netsuiteEndUseName || item.netsuiteDepartmentName || item.mrsNumber || '',
  ).trim();
  const source = full.replace(/^FOR\s+/i, '').trim();
  if (source.length <= max) return { tag: source, full };
  const words = source.split(/\s+/);
  let out = words[0] ?? '';
  for (let n = 1; n < words.length && `${out} ${words[n]}`.length <= max; n++) out += ` ${words[n]}`;
  return { tag: `${out}…`, full };
}

/** The legend's middle figure: what the picklist plans for the line, and what the MRS asked when that is more. */
export function plannedLabel(pick: number, requested: number, unit: string): string {
  return pick < requested ? `${pick} ${unit} planned (${requested} requested)` : `${pick} ${unit} planned`;
}

/**
 * ISS-R-028 — why Create is disabled while the cart holds items with nothing on hand (owner 2026-10-05): the user
 * removes them, and they wait on their MRS for a later picklist. "Nothing free", not "nothing on hand" (14ykpqtv7nd):
 * an item whose every unit another open picklist holds is Out too, and its shelf is not empty.
 */
export function outOfStockNotice(count: number): string {
  return count === 1
    ? '1 item has nothing free to pick — out of stock, or held by other open picklists. Remove it from the cart to create this picklist.'
    : `${count} items have nothing free to pick — out of stock, or held by other open picklists. Remove them from the cart to create this picklist.`;
}

/**
 * ISS-R-038 — how many rows of an allocation ask a bin for more than it has available. Allocate Bins does not
 * save while any does: the total can match the plan and still be impossible, and the server would refuse it only
 * when the picklist is created.
 */
export function rowsOverTheirBin(rows: readonly { allocated: number; available: number }[]): number {
  return rows.filter((r) => r.allocated > r.available).length;
}
